import React from 'react';
import Icon from '../../../components/AppIcon';
import StatusBadge from '../../../pages/order-management/components/StatusBadge';
import Button from '../../../components/ui/Button';

const QuoteCard = ({ quote, onView, onEdit, onConvert }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getExpirationStatus = () => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const expiration = quote?.expirationDate;
    
    if (expiration < today) {
      return { label: 'Vencida', color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' };
    } else if (expiration === today) {
      return { label: 'Vence hoy', color: 'text-orange-600', bgColor: 'bg-orange-100 dark:bg-orange-900/30' };
    } else {
      const daysUntilExpiration = Math.ceil((new Date(expiration) - new Date(today)) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiration <= 3) {
        return { label: `Vence en ${daysUntilExpiration} días`, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' };
      }
      return { label: `Válida hasta ${formatDate(expiration)}`, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' };
    }
  };

  const expirationStatus = getExpirationStatus();

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 hover:shadow-elevation-3 transition-smooth p-4 md:p-6 border border-border">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="text-base md:text-lg font-semibold text-foreground truncate">
              {quote?.customer}
            </h3>
            <StatusBadge status={quote?.status} />
            <span className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${expirationStatus?.color} ${expirationStatus?.bgColor}`}>
              {expirationStatus?.label}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Icon name="FileText" size={16} />
              Folio: {quote?.folio}
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="flex items-center gap-1">
              <Icon name="Calendar" size={16} />
              {formatDate(quote?.date)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(quote)}
            iconName="Eye"
            iconPosition="left"
          >
            Ver
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(quote)}
            iconName="Edit"
            iconPosition="left"
          >
            Editar
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={() => onConvert(quote)}
            iconName="CheckCircle"
            iconPosition="left"
          >
            Convertir a Orden
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Car" size={20} color="var(--color-primary)" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Vehículo</p>
            <p className="text-sm font-medium text-foreground truncate">
              {quote?.vehicle}
            </p>
            <p className="text-xs text-muted-foreground">{quote?.plates}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Phone" size={20} color="var(--color-accent)" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Teléfono</p>
            <p className="text-sm font-medium text-foreground truncate">
              {quote?.phone}
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Icon name="Package" size={16} color="var(--color-muted-foreground)" />
          <span className="text-sm text-muted-foreground">
            {quote?.itemCount} {quote?.itemCount === 1 ? 'artículo' : 'artículos'}
          </span>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground mb-1">Total Cotizado</p>
          <p className="text-lg md:text-xl font-bold text-foreground">
            {formatCurrency(quote?.total - (quote?.discount || 0))}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuoteCard;